import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Container, Row, Col } from 'react-bootstrap';

import { formatTodayHours } from '../utils/formatTodayHours';
import { restaurantCategories } from '../data/categories';
import { isRestaurantOpen } from '../utils/isRestaurantOpen';
import { fetchRestaurants } from '../api/apiService';

// Ensure this file is saved and imported correctly!
import '../styles/Restaurants.css'; 

const Restaurants = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState(location.state?.restaurants || []);
    const [filtered, setFiltered] = useState(location.state?.restaurants || []);
    const [activeCat, setActiveCat] = useState('All');

    const scrollRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const listRef = useRef(null);

    // --- 1. Data Loading Logic ---
    useEffect(() => {
        if (!location.state?.restaurants) {
            const loadRestaurants = async () => {
                try {
                    const response = await fetchRestaurants();
                    const arr = Array.isArray(response.data.restaurants) ? response.data.restaurants : [];
                    setRestaurants(arr);
                    setFiltered(arr);
                } catch (error) {
                    console.error('Error fetching restaurants:', error);
                }
            };
            loadRestaurants();
        }
    }, [location.state]);

    // --- 2. Filtering Logic ---
    const handleCategory = (cat) => {
        setActiveCat(cat);
        if (cat === 'All') {
            setFiltered(restaurants);
        } else {
            setFiltered(
                restaurants.filter(r => {
                    const lower = cat.toLowerCase();
                    return (
                        (r.name && r.name.toLowerCase().includes(lower)) ||
                        (r.cuisine && r.cuisine.toLowerCase().includes(lower)) ||
                        (Array.isArray(r.items) && r.items.some(item =>
                            (item.name && item.name.toLowerCase().includes(lower)) ||
                            (item.category && item.category.toLowerCase().includes(lower))
                        ))
                    );
                })
            );
        }
    };

    // --- 3. Scroll Logic ---
    const scrollCategory = (direction) => {
        const container = scrollRef.current;
        if (!container) return;
        const scrollAmount = 200;
        container.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const container = scrollRef.current;
        if (!container) return;
        setShowLeftArrow(container.scrollLeft > 10);
        setShowRightArrow(container.scrollLeft + container.offsetWidth < container.scrollWidth - 10);
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (container) {
            handleScroll();
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, []);

     const sortedFiltered = [...filtered].sort((a, b) => {
        const aOpen = isRestaurantOpen(
    a.operatingHours || a.timings,
    a.location?.country
);
        const bOpen = isRestaurantOpen(
    b.operatingHours || b.timings,
    b.location?.country
);
        if (aOpen === bOpen) return 0;
        return aOpen ? -1 : 1;
    });

    return (
        <Container className="restaurants-container">
            
            {/* --- HEADER FIX --- */}
            <div className="restaurants-header">
                <button
                    onClick={() => navigate(-1)}
                    className="back-btn"
                    aria-label="Go back"
                    type="button" // Explicitly declare type to prevent form submission behavior
                >
                    <FaChevronLeft />
                </button>
                <h2 className="page-title">Restaurants</h2>
            </div>

            {/* --- Category Scroller --- */}
            <div className="category-wrapper">
                {showLeftArrow && (
                    <button onClick={() => scrollCategory('left')} className="scroll-arrow left">
                        <FaChevronLeft size={14} />
                    </button>
                )}
                
                <div ref={scrollRef} className="category-list">
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleCategory('All')}
                        className={`cat-pill all`}
                        style={{
                            background: activeCat === 'All' ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : '#eee',
                            color: activeCat === 'All' ? '#fff' : '#222',
                            boxShadow: activeCat === 'All' ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                        }}
                    >
                        🍽️ All
                    </motion.div>

                    {restaurantCategories.map((cat, i) => (
                        <motion.div
                            key={i}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleCategory(cat.alt)}
                            className="cat-pill item"
                            style={{
                                background: activeCat === cat.alt ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' : '#fff',
                                boxShadow: activeCat === cat.alt ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                                border: activeCat === cat.alt ? '2px solid #e52e71' : '1px solid #eee',
                            }}
                        >
                            <img 
                                src={cat.src} 
                                alt={cat.alt} 
                                className="cat-img"
                                style={{ border: activeCat === cat.alt ? '2.5px solid #e52e71' : '2px solid #eee' }}
                            />
                            <div className="cat-name" style={{ color: activeCat === cat.alt ? '#e52e71' : '#222' }}>
                                {cat.alt}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {showRightArrow && (
                    <button onClick={() => scrollCategory('right')} className="scroll-arrow right">
                        <FaChevronRight size={14} />
                    </button>
                )}
            </div>

            {/* --- Restaurant Grid --- */}
            <div ref={listRef}>
                {sortedFiltered.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: '1.2rem', padding: '3rem 0' }}>
                        No restaurants found for this category.
                    </div>
                ) : (
                    <Row className="g-4">
                        {sortedFiltered.map(r => {
                            const scheduleData = r.operatingHours || r.timings;
                                const open = isRestaurantOpen(
                                    scheduleData,
                                    r.location?.country
                                );
                            
                            // Handle cuisine display logic
                            let cuisines = [];
                            if (Array.isArray(r.cuisine)) cuisines = r.cuisine;
                            else if (typeof r.cuisine === 'string') cuisines = r.cuisine.split(',').map(c => c.trim()).filter(Boolean);
                            else if (r.category) cuisines = [r.category];
                            
                            const cuisineDisplay = cuisines.slice(0, 2).join(', ');
                            const moreCuisines = cuisines.length > 2;

                            return (
                                <Col key={r._id} xs={12} md={6} lg={4} xl={3}>
                                    <motion.div whileHover={{ y: -5 }} className="restaurant-card">
                                        <Link to={`/restaurants/${r.slug || r._id}`} className="card-link">
                                            {r.deliveryTime && (
                                                <div className="badge-delivery">
                                                    <svg width="14" height="14" fill="none" stroke="#e05260" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    {r.deliveryTime}
                                                </div>
                                            )}
                                            <div className={`badge-status ${open ? 'open' : 'closed'}`}>
                                                {open ? 'Opened' : 'Closed'}
                                            </div>
                                            
                                            <img src={r.image || 'https://via.placeholder.com/240'} alt={r.name} className="card-image" />
                                            
                                            <div className="card-name">{r.name}</div>
                                            
                                            <div className="card-cuisine">
                                                {cuisineDisplay}
                                                {moreCuisines && <span style={{ opacity: 0.7, marginLeft: 4 }}>+ more</span>}
                                                {!cuisineDisplay && (r.category || 'Multi-cuisine')}
                                            </div>
                                            
                                           <div className="card-timings">
                                                <span>{formatTodayHours(scheduleData)}</span>
                                            </div>

                                
                                            <motion.button
                                                whileTap={{ scale: 0.95 }}
                                                className="btn-view-menu"
                                                style={{
                                                    background: open ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : '#ccc',
                                                    cursor: open ? 'pointer' : 'not-allowed',
                                                    opacity: open ? 1 : 0.7
                                                }}
                                                tabIndex={-1}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    navigate(`/restaurants/${r.slug || r._id}`);
                                                }}
                                            >
                                                View Menu
                                            </motion.button>
                                        </Link>
                                    </motion.div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
        </Container>
    );
};

export default Restaurants;